/**
 * リアルタイム再生エンジン。
 *
 * AudioContext は初回 PLAY で生成し（autoplay ポリシー対応）、
 * 25ms interval + 120ms lookahead の標準的な先読みスケジューラで
 * アレンジメント（メイン + SOUND A/B）の全イベントをスケジュールする。
 * フレーズを 1 回再生し、末尾のリリース + ディレイテールを待って自動停止する。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  flattenArrangement,
  type Arrangement,
  type ArrangementEvent,
} from "../utils/arrangement";
import { PPQ, ticksToSeconds } from "../utils/phraseGenerator";
import {
  createSynthChain,
  estimateDelayTail,
  scheduleVoice,
  wobbleSeedFor,
  type SynthChain,
  type SynthParams,
} from "../utils/synthVoice";

const SCHEDULER_INTERVAL_MS = 25;
const LOOKAHEAD_SEC = 0.12;
/** テール（リリース + ディレイ残響）の見積もりに足す安全マージン（秒） */
const TAIL_MARGIN_SEC = 0.3;

interface EngineConfig {
  arrangement: Arrangement;
  bpm: number;
  synthParams: SynthParams;
}

export interface SynthEngine {
  isPlaying: boolean;
  /** 再生ヘッド位置（tick）。停止中は null */
  playheadTick: number | null;
  play: () => void;
  stop: () => void;
}

export const useSynthEngine = (config: EngineConfig): SynthEngine => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadTick, setPlayheadTick] = useState<number | null>(null);

  const ctxRef = useRef<AudioContext | null>(null);
  const chainRef = useRef<SynthChain | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rafRef = useRef<number | null>(null);

  const events = useMemo(
    () => flattenArrangement(config.arrangement),
    [config.arrangement],
  );

  // スケジューラから常に最新の設定を読めるように ref へ写す
  const configRef = useRef(config);
  configRef.current = config;
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // 再生中の進行状態（再レンダリング不要のため ref）
  /** フレーズ先頭に対応する AudioContext 時刻 */
  const startTimeRef = useRef(0);
  const nextEventIndexRef = useRef(0);
  /** テールまで含めた終了予定時刻（全イベント発行後に確定する） */
  const endTimeRef = useRef<number | null>(null);

  const clearTimers = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const stop = useCallback(() => {
    clearTimers();
    // チェーンを破棄してスケジュール済みノート・ディレイテールを即座に切る
    chainRef.current?.dispose();
    chainRef.current = null;
    setIsPlaying(false);
    setPlayheadTick(null);
  }, []);

  const scheduleEvent = useCallback(
    (ctx: AudioContext, chain: SynthChain, ev: ArrangementEvent, startTime: number) => {
      const cfg = configRef.current;
      const { synthParams } = cfg;
      const osc =
        ev.layer === "main"
          ? synthParams.osc
          : ev.layer === "soundA"
            ? synthParams.soundAOsc
            : synthParams.soundBOsc;
      scheduleVoice(ctx, chain.input, {
        osc,
        midiNote: ev.note.midiNote,
        velocity: ev.note.velocity,
        startTime,
        durationSec: ticksToSeconds(ev.note.durationTicks, cfg.bpm),
        glideToMidi: ev.note.glideToMidi,
        glideSec: synthParams.glideMs / 1000,
        gold: synthParams.goldOn,
        goldRateHz: synthParams.goldRateHz,
        kaitlyn: synthParams.kaitlynOn,
        wobbleSeed: wobbleSeedFor(ev.layer, ev.note),
      });
    },
    [],
  );

  const play = useCallback(() => {
    const current = configRef.current;
    if (eventsRef.current.length === 0) return;

    if (!ctxRef.current) {
      ctxRef.current = new AudioContext();
    }
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") void ctx.resume();

    // 再生中に押し直された場合は組み直す
    chainRef.current?.dispose();
    clearTimers();

    chainRef.current = createSynthChain(
      ctx,
      ctx.destination,
      current.synthParams,
      current.bpm,
    );

    startTimeRef.current = ctx.currentTime + 0.06;
    nextEventIndexRef.current = 0;
    endTimeRef.current = null;
    setIsPlaying(true);

    timerRef.current = setInterval(() => {
      const cfg = configRef.current;
      const chain = chainRef.current;
      if (!chain) return;
      const horizon = ctx.currentTime + LOOKAHEAD_SEC;
      const evs = eventsRef.current;

      // 先読み範囲内のイベントを順にスケジュール
      while (nextEventIndexRef.current < evs.length) {
        const ev = evs[nextEventIndexRef.current];
        const startTime =
          startTimeRef.current + ticksToSeconds(ev.note.tick, cfg.bpm);
        if (startTime > horizon) break;
        scheduleEvent(ctx, chain, ev, startTime);
        nextEventIndexRef.current += 1;
      }

      // 全イベントを発行し終えたら、テール（リリース + ディレイ残響）を待って停止
      if (nextEventIndexRef.current >= evs.length) {
        if (endTimeRef.current === null) {
          const lastEndSec = evs.reduce((max, ev) => {
            const startSec = ticksToSeconds(ev.note.tick, cfg.bpm);
            const end =
              startSec + ticksToSeconds(ev.note.durationTicks, cfg.bpm);
            return Math.max(max, end);
          }, 0);
          endTimeRef.current =
            startTimeRef.current +
            lastEndSec +
            estimateDelayTail(cfg.synthParams, cfg.bpm) +
            TAIL_MARGIN_SEC;
        }
        if (ctx.currentTime >= endTimeRef.current) stop();
      }
    }, SCHEDULER_INTERVAL_MS);

    // 再生ヘッドの描画更新（表示用なので rAF で軽く回す）
    const updatePlayhead = () => {
      const cfg = configRef.current;
      const elapsed = ctx.currentTime - startTimeRef.current;
      const tick = (elapsed / (60 / cfg.bpm)) * PPQ;
      setPlayheadTick(
        tick >= 0 ? Math.min(tick, cfg.arrangement.lengthTicks) : 0,
      );
      rafRef.current = requestAnimationFrame(updatePlayhead);
    };
    rafRef.current = requestAnimationFrame(updatePlayhead);
  }, [scheduleEvent, stop]);

  // アンマウント時に完全解放
  useEffect(() => {
    return () => {
      clearTimers();
      chainRef.current?.dispose();
      chainRef.current = null;
      void ctxRef.current?.close();
      ctxRef.current = null;
    };
  }, []);

  return { isPlaying, playheadTick, play, stop };
};
